import { head, includes, join, omit, pipe, reject } from "ramda";
import { getNonAdminFields } from "../../content/edit/optionalContentFieldsUtils";
import { isBlank } from "../../../util/ext";
import { parseExtension } from "../../../components/FileUploadInput";

//-- Optional fields for UploadMediaDialog
export const optionalFieldsForUpload = [
  { fieldName: ["ageRange"], label: "Age Range" },
  {
    fieldName: ["attribution"],
    label: "Attribution",
  },
  { fieldName: ["audience"], label: "Audience" },
  {
    fieldName: ["doi"],
    label: "DOI",
  },
  {
    fieldName: ["episode"],
    label: "Episode",
  },
  {
    fieldName: ["isbn"],
    label: "ISBN",
    inputProps: { maxLength: 13 },
  },
  {
    fieldName: ["issn"],
    label: "ISSN",
    inputProps: { maxLength: 8 },
  },
  {
    fieldName: ["keywords"],
    label: "Keywords",
    size: 6,
  },
  {
    fieldName: ["periodical"],
    label: "Periodical",
  },
  {
    fieldName: ["placeOfPublication"],
    label: "Place of Publication",
  },
  {
    fieldName: ["purchaseLink", "title"],
    label: "Purchase Title",
  },
  {
    fieldName: ["purchaseLink", "url"],
    label: "Purchase URL",
  },
  {
    fieldName: ["rating"],
    label: "Rating",
  },
  { fieldName: ["series"], label: "Series" },
  {
    fieldName: ["sourceUrl"],
    label: "Source URL",
  },
  {
    fieldName: ["volume"],
    label: "Volume",
  },
];

const optionalUploadFieldsToExclude = {
  ARTICLE: [
    "episode",
    "issueNumber",
    "periodical",
    "rating",
    "volume",
    "volumeNumber",
  ],
  AUDIO: [
    "isbn",
    "issn",
    "issueNumber",
    "keywords",
    "internalPublisherId",
    "rating",
    "textDirectionality",
    "volume",
  ],
  AUDIOBOOK: ["edition", "episode", "isbn", "issueNumber", "volume"],
  BOOK: ["edition", "episode", "isbn", "issueNumber", "rating", "volume"],
  IMAGE: [
    "episode",
    "isbn",
    "issn",
    "issueNumber",
    "periodical",
    "placeOfPublication",
    "rating",
    "series",
    "sourceUrl",
    "textDirectionality",
    "volume",
  ],
  OTHER_DOCUMENT: ["episode", "issueNumber", "periodical", "rating", "volume"],
  VIDEO: [
    "episode",
    "isbn",
    "issn",
    "issueNumber",
    "keywords",
    "periodical",
    "series",
    "textDirectionality",
    "volume",
  ],
};

export const getOptionalFieldsForUpload = (type, isAdmin) =>
  pipe(
    reject(field => {
      const exclusions = optionalUploadFieldsToExclude[type];
      const fieldName = head(field.fieldName);
      return includes(fieldName, exclusions);
    }),
    fields => (isAdmin ? fields : getNonAdminFields(fields)),
  )(optionalFieldsForUpload);

export const submissionTypes = {
  ARTICLE: {
    accepts: [
      ".PDF",
      ".jpg",
      ".png",
      "application/pdf",
      "image/jpeg",
      "image/png",
    ],
    displayAccepts: [".PDF", ".jpg", ".png"],

    displayName: "Article",
  },
  AUDIO: {
    accepts: [
      ".MP3",
      "audio/mpeg3",
      "audio/x-mpeg-3",
      ".m4a",
      "audio/mpeg",
      ".wav",
      "audio/wav",
    ],
    displayAccepts: [".mp3", ".m4a", ".wav"],

    displayName: "Audio",
  },
  AUDIOBOOK: {
    accepts: [".ZIP", "application/zip", "application/x-zip-compressed"],
    displayAccepts: [".zip"],

    displayName: "Audiobook",
  },
  BOOK: {
    accepts: [".PDF", ".ePUB", "application/pdf", "application/epub+zip"],
    displayAccepts: ["ePub2", "ePub3", ".PDF"],

    displayName: "Book",
  },
  COVER: {
    accepts: [
      ".jpg",
      ".jpeg",
      "image/jpeg",
      ".png",
      "image/png",
      ".gif",
      "image/gif",
    ],
    displayAccepts: [".jpeg", ".jpg", ".gif", ".png"],
  },
  IMAGE: {
    accepts: [
      ".jpg",
      ".jpeg",
      "image/jpeg",
      ".png",
      "image/png",
      ".gif",
      "image/gif",
      ".tif",
      ".tiff",
      "image/tiff",
    ],
    displayAccepts: [".jpeg", ".jpg", ".gif", ".png", ".tiff", ".tif"],

    displayName: "Image",
  },
  OTHER_DOCUMENT: {
    accepts: [
      ".EPUB",
      "application/epub+zip",
      ".PDF",
      "application/pdf",
      ".jpg",
      "image/jpeg",
      ".tif",
      ".tiff",
      "image/tiff",
    ],
    displayAccepts: ["ePub2", "ePub3", ".PDF", ".tif", ".tiff", ".jpg"],

    displayName: "Document",
  },
  VIDEO: {
    accepts: [
      ".MP4",
      "video/mp4",
      ".MPG",
      "video/mpeg",
      ".MOV",
      "video/quicktime",
      ".AVI",
      "video/x-msvideo",
      ".3GP",
      "video/3gpp",
      ".FLV",
      "video/x-flv",
      ".DIVX",
      "video/divx",
      ".WEBM",
      "video/webm",
      ".OGG",
      "video/ogg",
    ],
    displayAccepts: [
      ".mp4",
      ".mpg",
      ".mov",
      ".avi",
      ".3gp",
      ".flv",
      ".divx",
      ".webm",
      ".ogg",
    ],
    displayName: "Video",
  },
};

//-- Maps lowercase file extensions to the mime types used as keys in the
//-- `maxFileSizes` object returned by STIP GET /submission-types. Extensions are
//-- transcribed from each submission type's `acceptsList`; the association is not
//-- derivable from list order (e.g. AUDIO lists ".m4a" ahead of "audio/wav").
export const extensionToMimeType = {
  ".3gp": "video/3gpp",
  ".avi": "video/x-msvideo",
  ".divx": "video/divx",
  ".epub": "application/epub+zip",
  ".flv": "video/x-flv",
  ".gif": "image/gif",
  ".jpeg": "image/jpeg",
  ".jpg": "image/jpeg",
  ".m4a": "audio/mpeg",
  ".mov": "video/quicktime",
  ".mp3": "audio/mpeg",
  ".mp4": "video/mp4",
  ".mpg": "video/mpeg",
  ".ogg": "video/ogg",
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".tif": "image/tiff",
  ".tiff": "image/tiff",
  ".wav": "audio/wav",
  ".webm": "video/webm",
  ".zip": "application/zip",
};

export const DEFAULT_MAX_FILE_SIZE = { size: 2000000000, display: "2 GB" };

//-- maxFileSizes: a submission type's `maxFileSizes`, keyed by mime type.
//-- Resolves by mime type first, then falls back to the file extension for the
//-- cases where the browser reports a blank or non-specific mime type
//-- (common for .epub, .divx and .m4a).
export const getMaxFileSize = (maxFileSizes, filename, mimeType) =>
  maxFileSizes?.[mimeType] ??
  maxFileSizes?.[extensionToMimeType[parseExtension(filename)]] ??
  DEFAULT_MAX_FILE_SIZE;

/**
 * Groups a submission type's accepted extensions by the limit that applies to
 * them, largest limit first, e.g. for IMAGE:
 * "2 GB (.tif, .tiff), 150 MB (.png), 100 MB (.jpeg, .jpg), 50 MB (.gif)"
 * acceptsList and maxFileSizes both come from STIP GET /submission-types.
 */
export const displayMaxFileSizes = (acceptsList, maxFileSizes) => {
  //-- keyed by display string so extensions sharing a limit group together
  const groups = new Map();
  const accepts = acceptsList ?? [];

  accepts
    .filter(entry => !includes("/", entry))
    .forEach(entry => {
      const extension = `.${entry.replace(/^\./, "").toLowerCase()}`;
      const maxFileSize = maxFileSizes?.[extensionToMimeType[extension]];
      if (!maxFileSize) return;

      const group = groups.get(maxFileSize.display);
      if (group) {
        group.extensions.push(extension);
      } else {
        groups.set(maxFileSize.display, {
          size: maxFileSize.size,
          extensions: [extension],
        });
      }
    });

  return Array.from(groups)
    .sort(([, a], [, b]) => b.size - a.size)
    .map(([display, { extensions }]) => {
      const sorted = extensions.sort().join(", ");
      return `${display} (${sorted})`;
    })
    .join(", ");
};

export const getAcceptedFileTypes = type =>
  join(", ", submissionTypes[type].accepts);

export const displayAcceptedMimeTypes = type => {
  const mimeTypes = join(", ", submissionTypes[type].displayAccepts);
  return `Accepted file types: ${mimeTypes}`;
};

// fileType: "SUBMISSION" | "COVER"
export const mapUploadFileData = (fileType, file) => ({
  filename: file.name,
  fileType: fileType,
  mimeType: file.type,
  size: file.size,
});

/**
 * STIP ingestion expects external links keyed by `type` with uppercase values,
 * while the BiblioBoard API uses `linkType`. Returns new objects so a retry
 * after a failed submission maps the same links again without corrupting the
 * ones held in media state.
 */
export const mapExternalLinksForIngestion = (links = []) =>
  links.map(({ linkType, ...link }) => ({
    ...omit(["type"], link),
    type: (linkType ?? link.type)?.toUpperCase(),
  }));

export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatTimeRemaining = secs => {
  if (secs === 0 || isBlank(secs) || isNaN(secs)) {
    return "";
  } else if (secs >= 60) {
    const minutes = Math.floor(secs / 60);
    return `${minutes} min left`;
  } else {
    return `${secs} secs left`;
  }
};
